import { hasRemoteImages, parseMailto, renderMail } from '../../lib/mail-html.js'
import { authRows, bareAddress, decorate, previewOf, saveBase64, sourceLabel } from '../../lib/mail.js'
import { formatBytes, fileIcon } from '../../lib/format.js'
import { routeHref } from '../../lib/router.js'
import { answerOf, defaultSlot, isCalendar, place, reasonText, today, whenText } from '../../lib/calendar.js'
import { fullDate, shortDate } from '../../lib/time.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-message ---
let __fez_component_0;
{
const VIEWS = [
  { id: 'html', label: 'HTML', icon: 'file-html' },
  { id: 'text', label: 'Text', icon: 'text-align-left' },
]
__fez_component_0 = class {
  PROPS = {
    container: { type: String, required: true },
    mailbox: String,
    email: { type: Object, required: true },
    // null until the full view loads; a bare Object prop would read as {} and pass for loaded.
    full: { type: Object, default: null },
    open: Boolean,
    addresses: Array,
    sources: Array,
    can_reply: { type: Boolean, default: true },
    on_toggle: { type: Function, state: true, default: () => {} },
    on_star: { type: Function, state: true, default: () => {} },
    on_respond: { type: Function, state: true, default: () => {} },
    on_changed: { type: Function, state: true, default: () => {} },
    on_images: { type: Function, state: true, default: () => {} },
  }

  init() {
    // The calendar card after Add, Apply or Ignore; until then the loaded message's own.
    this.state.card = undefined
    this.state.cardBusy = ''
    this.state.showImages = false
    this.state.imagesBusy = false
    this.state.raw = false
    this.state.rawShown = false
    this.state.messageId = ''
    this.state.details = false
    this.state.view = 'html'
  }

  afterRender() {
    const host = this.find('.msg-html')
    if (host) renderMail(host, this.state.bodyHtml, { remote: this.state.showImages || this.state.trusted, images: this.props.full?.images || {} })
  }

  onRefresh(props, changed) {
    // Another message in this card: its own calendar card, the source hidden and HTML shown.
    if (props.email.id !== this.state.messageId) {
      this.state.messageId = props.email.id
      this.state.card = undefined
      this.hideRaw()
      this.state.view = 'html'
    } else if ('open' in changed) {
      this.hideRaw()
      this.state.view = 'html'
    }
  }

  hideRaw() {
    this.state.raw = false
    this.state.rawShown = false
  }

  setView(view) {
    this.state.view = view
  }

  // Add to calendar, or Apply / Ignore a waiting change; then the card says what the calendar did.
  async calendarAction(action) {
    const card = this.state.card === undefined ? this.props.full?.calendar : this.state.card
    const params = action === 'event_add' ? { email: this.props.email.id } : { change: card?.change }
    this.state.cardBusy = action
    try {
      if (action === 'event_add') await window.App.api(`container/${this.props.container}/event_add`, { ...params, mailbox: this.props.mailbox || undefined })
      else await window.App.api(`calendar/${action}`, params)
      await this.reloadCard()
    } finally {
      this.state.cardBusy = ''
    }
  }

  async reloadCard() {
    const email = await window.App.api(`container/${this.props.container}/email`, { email: this.props.email.id, mailbox: this.props.mailbox || undefined }, { silent: true })
    this.state.card = email.calendar
  }

  toggle() {
    this.state.on_toggle(this.props.email.id)
  }

  showImages() {
    this.state.showImages = true
  }

  // Always show from the sender, or Stop: shared by everyone who reads the mailbox.
  async trustImages(trust) {
    const email = this.state.msg
    if (this.state.imagesBusy) return
    this.state.imagesBusy = true
    try {
      await window.App.api(`container/${this.props.container}/image_sender_${trust ? 'trust' : 'forget'}`, { email: email.id, mailbox: this.props.mailbox || undefined })
      this.state.showImages = false
      await this.state.on_images(email.id)
      window.Toast?.success(trust ? `Images from ${email.from_addr} always show` : `Images from ${email.from_addr} are hidden again`)
    } finally {
      this.state.imagesBusy = false
    }
  }

  toggleDetails() {
    this.state.details = !this.state.details
  }

  async download(id) {
    const file = this.props.full.files.find((item) => item.id === id)
    const result = await window.App.api(`container/${this.props.container}/file`, { file: id, mailbox: this.props.mailbox }, { silent: true })
    saveBase64(result.data, result.filename || file?.filename || (isCalendar(file?.content_type) ? 'invite.ics' : 'attachment'), result.content_type || 'application/octet-stream')
  }

  // Images and PDFs open in the app; anything else downloads.
  open(id) {
    const files = this.state.files.filter((file) => file.preview)
    const index = files.findIndex((file) => file.id === id)
    if (index < 0) return this.download(id)
    window.Dialog.open('file-preview-dialog', { container: this.props.container, mailbox: this.props.mailbox, files, index }, { title: files[index].label, size: 'xl' })
  }

  async exportEml() {
    const result = await window.App.api(`container/${this.props.container}/email_export`, { email: this.props.email.id, mailbox: this.props.mailbox }, { silent: true })
    saveBase64(result.data, result.filename, 'message/rfc822')
  }

  async retrySend() {
    await window.App.api(`container/${this.props.container}/send_retry`, { email: this.props.email.id, mailbox: this.props.mailbox })
    this.state.on_changed()
  }

  async takeBack() {
    await window.App.api(`container/${this.props.container}/send_cancel`, { email: this.props.email.id, mailbox: this.props.mailbox })
    this.state.on_changed()
  }

  // Create event: the form opens on this message's mailbox at the next full hour today, titled
  // by the subject, and the event links back to the message.
  async createEvent() {
    const email = this.state.msg
    const slot = defaultSlot(today())
    await window.Dialog.open('calendar-event-form', {
      mailbox: email.mailbox,
      email: email.id,
      title: email.subject || '',
      start: slot.start,
      end: slot.end,
    }, { title: 'Create event', size: 'md' })
  }

  async unsubscribe() {
    const email = this.props.full
    const target = email.unsubscribe
    if (target.one_click) {
      const ok = await window.Dialog.confirm({ title: 'Unsubscribe?', text: `Ask ${email.from_addr || 'the sender'} to stop sending this list. Your browser sends the request.`, confirm: 'Unsubscribe' })
      if (!ok) return
      const form = document.createElement('form')
      form.method = 'post'
      form.action = target.http
      form.target = '_blank'
      form.rel = 'noreferrer noopener'
      const field = document.createElement('input')
      field.type = 'hidden'
      field.name = 'List-Unsubscribe'
      field.value = 'One-Click'
      form.append(field)
      document.body.append(form)
      form.submit()
      form.remove()
      window.Toast.success('Unsubscribe request sent')
    } else if (target.http) {
      window.open(target.http, '_blank', 'noopener,noreferrer')
    } else if (target.mailto) {
      const mail = parseMailto(target.mailto)
      window.App.go(routeHref('compose', { container: this.props.container, mailbox: email.mailbox, to: mail.to, subject: mail.subject || 'unsubscribe', body: mail.body }))
    }
  }

  // Source stays mounted once shown, so hiding and showing it again loads it once.
  toggleRaw() {
    this.state.raw = !this.state.raw
    if (this.state.raw) this.state.rawShown = true
  }

  openSender() {
    return this.openAddress(this.state.msg.from_addr)
  }

  // Spam decisions and writes go through the message's mailbox; my contact's name, when I have one, heads it.
  async openAddress(address) {
    if (!address) return
    const container = this.props.container
    const mailbox = this.state.msg.mailbox
    const [senders, found] = await Promise.all([
      window.App.api(`container/${container}/senders`, { mailbox, address }, { silent: true }),
      this.globalState.me ? window.App.api('contact/list', { address }, { silent: true }) : { items: [] },
    ])
    const name = found.items[0]?.name || ''
    const result = await window.Dialog.open('address-dialog', { container, mailbox, address, name, decisions: senders.items }, { title: name || address })
    if (result === 'write') window.App.go(routeHref('compose', { container, mailbox, to: address }))
    else if (result === 'show') window.App.go(routeHref('inbox', { container, mailbox, folder: 'all', q: address }))
  }

  // What the card shows for the server's calendar view of this message.
  calendarCard(email, card) {
    if (!card) return null
    const placed = card.start ? place(card) : null
    const answer = card.reply ? answerOf(card.reply.partstat) : null
    const who = card.reply?.name || card.reply?.address || 'Someone'
    const states = {
      added: ['In your calendar', 'success', ''],
      current: ['In your calendar', 'success', ''],
      updated: ['Updated', 'accent', ''],
      older: ['Older version', 'neutral', 'Your calendar has a newer version of this event.'],
      cancelled: ['Cancelled', 'danger', card.instance ? 'This one time is off your calendar.' : 'The event is cancelled in your calendar.'],
      replied: [`${who}: ${answer?.label || card.reply?.partstat || 'answered'}`, answer?.tone || 'neutral', ''],
      waiting: ['Update waiting', 'warning', `${reasonText(card.reason)} Apply it only if you trust it.`],
      ignored: ['Ignored', 'neutral', 'You ignored this change; the calendar stays as it was.'],
    }
    const loose = { REQUEST: ['Invitation', 'accent'], CANCEL: ['Cancelled', 'danger'], REPLY: ['Reply', 'neutral'] }[card.method] || ['Event', 'neutral']
    let [badge, tone, note] = states[card.state] || [...loose, card.event ? 'Your calendar has not taken this message.' : 'Not in your calendar.']
    if (card.event_status === 'cancelled' && card.state !== 'cancelled') {
      badge = 'Cancelled'
      tone = 'danger'
      note = 'The event was cancelled since.'
    }
    const file = (email.files || []).find((item) => isCalendar(item.content_type, item.filename))
    const people = card.attendees ? `${card.attendees} invited` : ''
    return {
      title: card.summary || '(no title)',
      when: placed ? whenText(placed) : '',
      guessed: card.zone_guessed,
      where: card.location,
      who: [card.organizer_name || card.organizer, people].filter(Boolean).join(', '),
      url: card.url,
      badge,
      tone,
      note,
      waiting: card.state === 'waiting' && card.change,
      canAdd: card.state === 'none' && !card.event,
      href: card.event ? routeHref('calendar', { date: placed?.firstDay, event: card.event, calendar: card.calendar || email.mailbox }) : '',
      file: file?.id || '',
      answer: card.answerable && card.event ? { event: card.event, partstat: card.my_partstat || '' } : null,
    }
  }

  beforeRender() {
    const email = decorate({ ...this.props.email, ...(this.props.full || {}) })
    this.state.msg = email
    this.state.when = shortDate(email.received_at)
    this.state.fullDate = fullDate(email.received_at)
    this.state.fromLine = email.from_addr && email.from_name ? email.from_addr : ''
    const list = (value) => (value || []).map((item) => item.address).filter(Boolean)
    this.state.toLine = list(email.to).join(', ') || email.address
    this.state.ccLine = list(email.cc).join(', ')
    // Mail that reached us through an alias or Bcc says where it was delivered.
    const named = list(email.to).concat(list(email.cc)).map(bareAddress)
    this.state.deliveredTo = email.direction === 'in' && !named.includes(bareAddress(email.address)) ? email.address : ''
    const source = email.source && this.props.sources.find((item) => item.ref === email.source)
    this.state.forwardedBy = source ? sourceLabel(source) : ''
    const full = this.props.full
    this.state.loaded = Boolean(full)
    this.state.views = VIEWS
    this.state.hasText = Boolean(full && full.text_html)
    // Mail with an HTML part opens on it and offers its text; plain-text mail is its text.
    const html = full ? (this.state.hasText && this.state.view === 'text' ? full.text_html : full.body_html) || '' : ''
    const remote = Boolean(html) && hasRemoteImages(html)
    this.state.trusted = Boolean(full?.images_trusted)
    this.state.remote = remote && !this.state.showImages && !this.state.trusted
    this.state.trustedLine = remote && this.state.trusted
    this.state.canTrust = Boolean(email.from_addr) && email.folder !== 'spam'
    this.state.bodyHtml = html
    this.state.auth = full ? authRows(full.auth) : []
    // The files the body shows have a `url`; the alternative text/calendar part has no name: the invite card shows it instead.
    this.state.files = (full ? full.files : []).filter((file) => !file.url && !(isCalendar(file.content_type, file.filename) && !file.filename)).map((file) => ({ ...file, icon: fileIcon(file.content_type, file.filename), label: file.filename || 'attachment', meta: formatBytes(file.size), preview: previewOf(file.content_type, file.filename) }))
    this.state.sendNote = email.send_status === 'scheduled' ? `Goes out ${fullDate(email.received_at)}.` : email.send_error || 'Waiting for the outbound transport.'
    this.state.cardView = full ? this.calendarCard(email, this.state.card === undefined ? full.calendar : this.state.card) : null
    this.state.canRetry = email.send_status === 'failed'
    this.state.canTakeBack = email.send_status === 'scheduled' || email.send_status === 'held'
  }

  CSS = `:fez {
display:block;.msg{border:1px solid var(--color-border);border-radius:var(--radius-lg);background:var(--color-bg-raised);box-shadow:var(--shadow-sm);overflow:hidden}.msg-line{display:flex;align-items:center;gap:var(--space-3);width:100%;padding:var(--space-3) var(--space-4);border:0;background:transparent;color:inherit;font:inherit;text-align:left;cursor:pointer;&:hover{background:var(--color-bg-hover)}}.msg-line-who{flex:0 0 auto;max-width:40%;overflow:hidden;font-weight:var(--weight-semibold);text-overflow:ellipsis;white-space:nowrap}.msg-line-preview{flex:1;min-width:0;overflow:hidden;color:var(--color-text-muted);font-size:var(--text-sm);text-overflow:ellipsis;white-space:nowrap}.msg-when{flex-shrink:0;color:var(--color-text-subtle);font-size:var(--text-xs);font-variant-numeric:tabular-nums}.msg-unread .msg-line-who{font-weight:var(--weight-bold)}.msg-head{display:flex;flex-wrap:wrap;align-items:flex-start;gap:var(--space-3);padding:var(--space-4) var(--space-4) var(--space-3);cursor:pointer}.msg-modes{flex:1 0 100%;padding-left:calc(36px + var(--space-3));cursor:default}.msg-who{display:grid;gap:2px;flex:1;min-width:0}.msg-from{display:flex;flex-wrap:wrap;align-items:baseline;gap:0 var(--space-2);min-width:0}.msg-name{font-weight:var(--weight-semibold)}.msg-name-link{padding:0;border:0;background:transparent;color:inherit;font:inherit;font-weight:var(--weight-semibold);text-align:left;cursor:pointer;&:hover{text-decoration:underline;text-underline-offset:2px}}.msg-addr,.msg-to{overflow:hidden;color:var(--color-text-muted);font-size:var(--text-sm);text-overflow:ellipsis;white-space:nowrap}.msg-via{color:var(--color-accent)}.msg-tools{display:flex;align-items:center;gap:2px;flex-shrink:0}.msg-tools-when{margin-right:var(--space-2)}.msg-star{display:grid;place-items:center;width:30px;height:30px;padding:0;border:0;border-radius:var(--radius-md);background:transparent;color:var(--color-text-muted);cursor:pointer;&:hover{background:var(--color-bg-hover);color:var(--color-text)}&.on{color:var(--color-warning)}}.msg-details{display:grid;gap:var(--space-2);margin:0 var(--space-4) var(--space-3);padding:var(--space-3);border-radius:var(--radius-md);background:var(--color-bg-subtle);color:var(--color-text-muted);font-size:var(--text-sm)}.msg-details-row{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2)}.msg-note{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2) var(--space-3);margin:0 var(--space-4) var(--space-3);padding:var(--space-2) var(--space-3);border-radius:var(--radius-md);background:var(--color-bg-subtle);color:var(--color-text-muted);font-size:var(--text-sm)}.msg-note-text{flex:1 1 16em;min-width:0;overflow-wrap:anywhere}.msg-note-actions{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2) var(--space-3);min-width:0}.msg-note-link{min-width:0;padding:0;border:0;background:transparent;color:var(--color-accent);font:inherit;font-weight:var(--weight-medium);text-align:left;overflow-wrap:anywhere;cursor:pointer;&:hover{text-decoration:underline;text-underline-offset:2px}}.msg-invite{display:flex;gap:var(--space-3);margin:0 var(--space-4) var(--space-3);padding:var(--space-3) var(--space-4);border:1px solid var(--color-border);border-left:3px solid var(--color-accent);border-radius:var(--radius-md);background:var(--color-bg-subtle)}.msg-invite-icon{display:flex;flex:none;padding-top:2px;color:var(--color-accent)}.msg-invite-text{display:grid;flex:1;gap:2px;min-width:0;font-size:var(--text-sm)}.msg-invite-title{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2);font-size:var(--text-md);font-weight:var(--weight-semibold)}.msg-invite-line{color:var(--color-text-muted)}.msg-invite-note{color:var(--color-text-muted);font-style:italic}.msg-invite.waiting{border-left-color:var(--color-warning);.msg-invite-icon{color:var(--color-warning)}}.msg-invite.cancelled{border-left-color:var(--color-danger);.msg-invite-icon{color:var(--color-danger)}}.msg-invite-actions{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2);margin-top:var(--space-2)}.msg-invite-answer{margin-top:var(--space-2)}.msg-html{display:block;contain:inline-size paint;border-top:1px solid var(--color-border)}.msg-empty{padding:0 var(--space-4) var(--space-4);color:var(--color-text-subtle);font-size:var(--text-sm)}.msg-loading{display:flex;justify-content:center;padding:var(--space-6)}.msg-raw{padding:var(--space-3) var(--space-4) var(--space-4);border-top:1px solid var(--color-border)}.msg-files{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:var(--space-2);padding:var(--space-3) var(--space-4) var(--space-4);border-top:1px solid var(--color-border)}.msg-file{display:flex;align-items:center;gap:var(--space-2);min-width:0;padding:var(--space-2) var(--space-3);border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg);color:var(--color-text);font:inherit;text-align:left;cursor:pointer;&:hover{border-color:var(--color-border-strong);background:var(--color-bg-hover)}}.msg-file-icon{display:flex;flex:none;color:var(--color-accent)}.msg-file-text{display:grid;min-width:0}.msg-file-name,.msg-file-meta{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.msg-file-name{font-size:var(--text-sm);font-weight:var(--weight-medium)}.msg-file-meta{color:var(--color-text-muted);font-size:var(--text-xs)} :mobile {.msg-head{flex-wrap:wrap;padding:var(--space-3) var(--space-3) var(--space-2)}.msg-who{flex:1 1 0}.msg-tools{flex:1 0 100%;justify-content:flex-end}.msg-tools-when{margin-right:auto;padding-left:calc(36px + var(--space-3))}}
}`

  HTML = `<article class="msg" class:msg-unread={state.msg.unread} class:msg-open={props.open}>
{#if props.open}
<div class="msg-head" onclick="fez.toggle()">
<ui-avatar name={state.msg.from_name || state.msg.from_addr || '?'} size="md"></ui-avatar>
<div class="msg-who">
<div class="msg-from">
{#if state.msg.from_addr}
<button type="button" class="msg-name msg-name-link" title="Block or write" onclick="event.stopPropagation(); fez.openSender()">{state.msg.from_name || state.msg.from_addr}</button>
{:else}
<span class="msg-name">{state.msg.from_name || 'Unknown sender'}</span>
{/if}
{#if state.fromLine}<span class="msg-addr">{state.fromLine}</span>{/if}
</div>
<div class="msg-to" title={state.toLine}>
to {state.toLine}{#if state.ccLine}, cc {state.ccLine}{/if}
{#if state.forwardedBy}<span class="msg-via"> forwarded by {state.forwardedBy}</span>{/if}
{#if state.deliveredTo}<span class="msg-via"> via {state.deliveredTo}</span>{/if}
</div>
</div>
<div class="msg-tools" onclick="event.stopPropagation()">
<span class="msg-when msg-tools-when" title={state.fullDate}>{state.when}</span>
{#if props.can_reply}
<ui-button size="sm" variant="ghost" icon="arrow-bend-up-left" title="Reply (r)" onclick="fez.state.on_respond('reply', '{state.msg.id}')"></ui-button>
<ui-button size="sm" variant="ghost" icon="arrow-bend-double-up-left" title="Reply all (a)" onclick="fez.state.on_respond('all', '{state.msg.id}')"></ui-button>
<ui-button size="sm" variant="ghost" icon="arrow-bend-up-right" title="Forward (f)" onclick="fez.state.on_respond('forward', '{state.msg.id}')"></ui-button>
{/if}
<button type="button" class="msg-star" class:on={state.msg.flagged} title={state.msg.flagged ? 'Unstar (s)' : 'Star (s)'} aria-label={state.msg.flagged ? 'Unstar' : 'Star'} onclick="fez.state.on_star('{state.msg.id}')">
{#if state.msg.flagged}<ui-icon name="star" weight="fill" size="16"></ui-icon>{:else}<ui-icon name="star" size="16"></ui-icon>{/if}
</button>
<ui-button size="sm" variant="ghost" icon="calendar-plus" title="Create event" onclick="fez.createEvent()"></ui-button>
<ui-button size="sm" variant="ghost" icon="info" title={state.details ? 'Hide details' : 'Details'} onclick="fez.toggleDetails()"></ui-button>
</div>
{#if state.hasText}<div class="msg-modes" onclick="event.stopPropagation()"><ui-segmented label="Show the message as" :options="state.views" value={state.view} :onchange="(view) => fez.setView(view)"></ui-segmented></div>{/if}
</div>
{#if state.details}
<div class="msg-details" fez:in="slide, duration=160" fez:out="slide, duration=120">
<div class="msg-details-row">{state.fullDate}{#if state.loaded} - {state.msg.address}{/if}</div>
{#if state.auth.length}
<div class="msg-details-row">
{#each state.auth as row}
<ui-badge tone={row.tone} label={row.label}></ui-badge>
{/each}
</div>
{/if}
<div class="msg-details-row">
<ui-button size="sm" icon="download-simple" label="Download .eml" onclick="fez.exportEml()"></ui-button>
{#if state.loaded}
<ui-button size="sm" variant="ghost" icon="code" label={state.raw ? 'Hide source' : 'Source'} onclick="fez.toggleRaw()"></ui-button>
{/if}
{#if state.msg.unsubscribe}
<ui-button size="sm" icon="bell-slash" label="Unsubscribe" onclick="fez.unsubscribe()"></ui-button>
{/if}
</div>
</div>
{/if}
{#if state.msg.sendLabel}
<div class="msg-note">
<ui-badge tone={state.msg.sendTone} label={state.msg.sendLabel}></ui-badge>
<span class="msg-note-text">{state.sendNote}</span>
{#if state.canRetry}<ui-button size="sm" icon="arrows-clockwise" label="Try again" onclick="fez.retrySend()"></ui-button>{/if}
{#if state.canTakeBack}<ui-button size="sm" icon="arrow-u-up-left" label="Take back" onclick="fez.takeBack()"></ui-button>{/if}
</div>
{/if}
{#if state.remote}
<div class="msg-note">
<ui-icon name="image" size="16"></ui-icon>
<span class="msg-note-text">Remote images are hidden to protect your privacy.</span>
<span class="msg-note-actions">
<ui-button size="sm" label="Show images" onclick="fez.showImages()"></ui-button>
{#if state.canTrust}
<button type="button" class="msg-note-link" onclick="fez.trustImages(true)">Always show from {state.msg.from_addr}</button>
{/if}
</span>
</div>
{/if}
{#if state.trustedLine}
<div class="msg-note">
<ui-icon name="image" size="16"></ui-icon>
<span class="msg-note-text">Images shown: {state.msg.from_addr} is trusted.</span>
<ui-button size="sm" variant="ghost" label="Stop" loading={state.imagesBusy} onclick="fez.trustImages(false)"></ui-button>
</div>
{/if}
{#if state.cardView}
<div class="msg-invite" class:waiting={state.cardView.waiting} class:cancelled={state.cardView.tone === 'danger'}>
<span class="msg-invite-icon"><ui-icon name="calendar-blank" size="22"></ui-icon></span>
<div class="msg-invite-text">
<div class="msg-invite-title">{state.cardView.title}<ui-badge tone={state.cardView.tone} label={state.cardView.badge}></ui-badge></div>
{#if state.cardView.when}<div>{state.cardView.when}</div>{/if}
{#if state.cardView.guessed}<div class="msg-invite-line">The invite's time zone is unknown: the time is shown as written.</div>{/if}
{#if state.cardView.where}<div class="msg-invite-line">{state.cardView.where}</div>{/if}
{#if state.cardView.who}<div class="msg-invite-line">{state.cardView.who}</div>{/if}
{#if state.cardView.note}<div class="msg-invite-note">{state.cardView.note}</div>{/if}
{#if state.cardView.answer && props.can_reply}
<div class="msg-invite-answer"><calendar-answer calendar={state.msg.mailbox} event={state.cardView.answer.event} partstat={state.cardView.answer.partstat} :on_answered="() => fez.reloadCard()"></calendar-answer></div>
{/if}
<div class="msg-invite-actions">
{#if state.cardView.waiting}
<ui-button size="sm" variant="primary" icon="check" label="Apply" loading={state.cardBusy === 'event_change_apply'} onclick="fez.calendarAction('event_change_apply')"></ui-button>
<ui-button size="sm" label="Ignore" loading={state.cardBusy === 'event_change_ignore'} onclick="fez.calendarAction('event_change_ignore')"></ui-button>
{/if}
{#if state.cardView.canAdd}
<ui-button size="sm" variant="primary" icon="calendar-plus" label="Add to calendar" loading={state.cardBusy === 'event_add'} onclick="fez.calendarAction('event_add')"></ui-button>
{/if}
{#if state.cardView.url}
<ui-button size="sm" icon="video-camera" label="Join" href={state.cardView.url} target="_blank" rel="noreferrer noopener"></ui-button>
{/if}
{#if state.cardView.href}
<ui-button size="sm" icon="calendar-blank" label="Open in calendar" href={state.cardView.href}></ui-button>
{/if}
{#if state.cardView.file}
<ui-button size="sm" variant="ghost" icon="download-simple" label="Download .ics" onclick="fez.download('{state.cardView.file}')"></ui-button>
{/if}
</div>
</div>
</div>
{/if}
{#if !state.loaded}
<div class="msg-loading"><ui-spinner size="sm"></ui-spinner></div>
{:else}
{#if state.bodyHtml}
<div class="msg-html"></div>
{:else}
<div class="msg-empty">This message has no text.</div>
{/if}
{/if}
{#if state.rawShown}
<div class="msg-raw" hidden={!state.raw}>
<mail-raw container={props.container} mailbox={props.mailbox} email={state.msg.id} :view="props.full"></mail-raw>
</div>
{/if}
{#if state.files.length}
<div class="msg-files">
{#each state.files as file}
<button type="button" class="msg-file" key={file.id} onclick="fez.open('{file.id}')">
<span class="msg-file-icon"><ui-icon name={file.icon} size="22"></ui-icon></span>
<span class="msg-file-text">
<span class="msg-file-name">{file.label}</span>
<span class="msg-file-meta">{file.meta}</span>
</span>
</button>
{/each}
</div>
{/if}
{:else}
<button type="button" class="msg-line" onclick="fez.toggle()">
<ui-avatar name={state.msg.from_name || state.msg.from_addr || '?'} size="sm"></ui-avatar>
<span class="msg-line-who">{state.msg.direction === 'out' ? 'Me' : state.msg.who}</span>
<span class="msg-line-preview">{state.msg.preview || state.msg.subject || ''}</span>
<span class="msg-when" title={state.fullDate}>{state.when}</span>
</button>
{/if}
</article>`
};
}

Fez('mail-message', __fez_component_0);

export default __fez_component_0;
